import React, { ErrorInfo } from 'react'
import styled from '@emotion/styled'
import deepEqual from 'lodash/isEqual'
import { LogLevel } from 'src/common-services'
import { Button, Container } from '@mui/material'
import { Alert, AlertTitle } from '@mui/material'
import { loggingClient } from 'src/clients'
import { Spinner2 } from '@emotion-icons/evil/Spinner2'
import { isAdmin } from 'src/components/auth/util'
import { useSetRecoilState } from 'recoil'
import { retryAtom } from 'src/utils/recoil'
import { EXPIRED_URL_PARAM, LOGGED_IN_ELSEWHERE_PARAM, LOGOUT_PATH } from 'src/utils/constants'
import { isAuthError } from 'src/utils/errors'
import { NextRouter, withRouter } from 'next/router'
import { getQuery, redirect } from 'src/layout/hooks/use-parsed-router'
import { FullScreenLoader } from 'src/layout/full-screen-loader'

type ErrorBoundaryProps = {
  children: React.ReactNode
  router: NextRouter
  wrap?: boolean
}

type AuthErrorState = {
  authError: boolean
}

type State = {
  error: Error | undefined
  errorInfo: ErrorInfo | undefined
  loading: boolean
  showDetails: boolean
  retryCount: number
  authError: boolean
}

type ErrorDetailsPros = {
  error: Error | undefined
  errorInfo: ErrorInfo | undefined
  showDetails: boolean
}

const FakeLink = styled.span`
  font-size: 10px;
  color: #888;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

const Retry: React.FC = () => {
  const updateRetryAtom = useSetRecoilState(retryAtom)
  React.useEffect(() => {
    updateRetryAtom((old) => old + 1)
  }, [])
  return null
}

const ErrorDetails: React.FC<ErrorDetailsPros> = (props) => {
  const { error, errorInfo, showDetails } = props
  const show = showDetails
  if (!show) {
    return null
  }
  return (
    <div>
      <div style={{ padding: 20 }}>
        <strong>error:</strong>
      </div>
      <code>{error?.message}</code>
      <code>{error?.stack}</code>
      <div style={{ padding: 20 }}>
        <strong>component stack:</strong>
      </div>
      <code>{errorInfo?.componentStack}</code>
    </div>
  )
}

type ErrorButtonProps = {
  onClick: () => void
  icon?: React.ReactNode
  children: string
  wrap?: boolean
}

export const ErrorBoundaryButton = (props: ErrorButtonProps) => {
  const { onClick, children, icon, wrap } = props
  if (icon) {
    return (
      <Button color="primary" variant="contained" size={wrap ? 'small' : 'medium'} onClick={onClick} endIcon={icon}>
        {children}
      </Button>
    )
  }
  return (
    <Button color="primary" variant="contained" size={wrap ? 'small' : 'medium'} onClick={onClick}>
      {children}
    </Button>
  )
}

class InnerAuthErrorBoundary extends React.Component<ErrorBoundaryProps, AuthErrorState> {
  state = {
    authError: false,
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const authError = isAuthError(error)
    if (authError) {
      if (error.status === 401) {
        redirect(LOGOUT_PATH)
      }
      if (error.status === 402) {
        redirect(LOGOUT_PATH, { [EXPIRED_URL_PARAM]: 'true' })
      }
      if (error.status === 409) {
        redirect(LOGOUT_PATH, { [LOGGED_IN_ELSEWHERE_PARAM]: 'true' })
      }
    }
    this.setState({ authError })
  }

  render() {
    if (this.state.authError) {
      return <FullScreenLoader />
    }
    return <>{this.props.children}</>
  }
}

class InnerErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  state: State = {
    error: undefined,
    errorInfo: undefined,
    loading: false,
    showDetails: isAdmin(),
    retryCount: 0,
    authError: false,
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    void loggingClient.logData({
      level: LogLevel.ERROR,
      message: error.message,
      data: {
        name: error.name,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        locationHash: location.hash,
        locationPathname: location.pathname,
        locationSearch: location.search,
      },
    })
    const authError = isAuthError(error)
    if (authError) {
      if (error.status === 409) {
        redirect(LOGOUT_PATH, { [LOGGED_IN_ELSEWHERE_PARAM]: 'true' })
      }
    }
    this.setState({ error, errorInfo, authError })
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (!this.state.error) {
      return
    }

    const query = getQuery(this.props.router.asPath)
    const prevQuery = getQuery(prevProps.router.asPath)
    const pathDifferent = prevProps.router.pathname !== this.props.router.pathname
    const searchDifferent = !deepEqual(query, prevQuery)

    // if the route changes, reset the error otherwise you can no longer route after an error
    if (pathDifferent || searchDifferent) {
      this.setState({ error: undefined, errorInfo: undefined })
    }
  }

  tryAgain = () => {
    if (this.state.retryCount > 0) {
      this.props.router.reload()
    }
    this.setState({ loading: true, retryCount: this.state.retryCount + 1 })
    setTimeout(() => {
      this.setState({ error: undefined, errorInfo: undefined, loading: false })
    }, 1000)
  }

  goBack = () => {
    if (this.state.retryCount > 0) {
      redirect('/')
    }
    this.setState({ retryCount: this.state.retryCount + 1 })
    this.props.router.back()
  }

  showDetails = () => {
    this.setState({ showDetails: true })
  }

  render() {
    if (!this.state.error) {
      return <>{this.props.children}</>
    }

    if (this.state.authError) {
      return <FullScreenLoader />
    }

    const icon = this.state.loading ? <Spinner2 className="spin" size={16} /> : null
    return (
      <>
        <Container
          style={{
            padding: 20,
            maxHeight: this.props.wrap ? 200 : undefined,
            overflow: this.props.wrap ? 'auto' : undefined,
          }}>
          <Alert severity="error">
            <AlertTitle>Unhandled error</AlertTitle>
            <p>
              Sorry! Something unexpected happened which caused an unhandled error —{' '}
              <strong>our support team has been notified</strong>.
              {!this.state.showDetails && (
                <FakeLink style={{ paddingLeft: 5 }} onClick={this.showDetails}>
                  view details
                </FakeLink>
              )}
            </p>
            <p>
              <span style={{ paddingRight: 10 }}>You can</span>
              <ErrorBoundaryButton onClick={this.tryAgain} icon={icon} wrap={this.props.wrap}>
                Try Again
              </ErrorBoundaryButton>
              {this.state.loading && <Retry />}
              <span style={{ padding: 10 }}>or</span>
              <ErrorBoundaryButton onClick={this.goBack} wrap={this.props.wrap}>
                Go Back
              </ErrorBoundaryButton>
            </p>
            <ErrorDetails {...this.state} />
          </Alert>
        </Container>
      </>
    )
  }
}

// The type Definition for withRouter is incorrect, so we need to cast it to any
export const ErrorBoundary = withRouter(InnerErrorBoundary) as any
export const AuthErrorBoundary = withRouter(InnerAuthErrorBoundary) as any

type CustomProps = {
  errorDisplay: React.ReactNode
  children?: React.ReactNode
}
export class CustomErrorBoundary extends React.Component<CustomProps, any> {
  state = { error: false }
  componentDidCatch(error: Error) {
    this.setState({ error: true })
  }

  render() {
    if (this.state.error) {
      return <>{this.props.errorDisplay}</>
    }
    return <>{this.props.children}</>
  }
}
